// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { type LostTraysReasonType, lostTraysReasonToDisplayValue, utils } from '@seedplanner/shared-utils';
import * as React from 'react';
import { getID } from '../../utils';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import CropIdLink from '../crop/CropIdLink';
import { PlantingGroupIdLink } from '../plantingGroup';
import LostTraysIncidentIdLink from './LostTraysIncidentIdLink';

interface LostTraysData {
  readonly id: string;
  readonly trays_lost: number;
  readonly reason: string;
  readonly trays_lost_at: number;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
  readonly planting_id?: string;
}
interface Props {
  readonly lostTrays: LostTraysData[];
  readonly hidePlantingGroup?: boolean;
}

export default function LostTraysTable({ lostTrays, hidePlantingGroup }: Props) {
  const columns: ColumnType[] = [
    {
      name: 'Incident',
      values: lostTrays.map((lostTrays) => (
        <VisibleAtContainer
          key={lostTrays.id}
          up={<LostTraysIncidentIdLink id={getID(lostTrays.id)} />}
          down={<LostTraysIncidentIdLink id={getID(lostTrays.id)} title={getID(lostTrays.id)} />}
        />
      )),
    },
    {
      name: 'Crop',
      values: lostTrays.map((lostTrays) =>
        lostTrays.crop ? (
          <CropIdLink key={lostTrays.crop.id} id={getID(lostTrays.crop.id)} name={lostTrays.crop.crop_name} />
        ) : (
          'Unknown'
        ),
      ),
    },
    {
      name: 'Reason',
      values: lostTrays.map((lostTrays) => lostTraysReasonToDisplayValue(lostTrays.reason as LostTraysReasonType)),
    },
    {
      name: 'Trays Lost',
      smallName: 'Trays',
      values: lostTrays.map((lostTrays) => lostTrays.trays_lost),
    },
    {
      name: 'Lost On',
      values: lostTrays.map((lostTrays) => <Time key={lostTrays.id} time={lostTrays.trays_lost_at} />),
      visibleAt: 'sm' as const,
    },
    hidePlantingGroup
      ? null
      : {
          name: 'Planting Group',
          smallName: 'Group',
          values: lostTrays.map((lostTrays) =>
            lostTrays.planting_id ? (
              <PlantingGroupIdLink key={lostTrays.planting_id} id={lostTrays.planting_id} />
            ) : (
              'Unknown'
            ),
          ),
          visibleAt: 'md' as const,
        },
  ].filter(utils.notNull);

  return <Table columns={columns} />;
}
